<template>
    <form-group :horizontal="horizontal" :label="label" :label-classes="[labelClasses, { required }]">
        <input
            :disabled="disabled"
            :value="modelValue?.toFormat('HH:mm')"
            class="form-control"
            placeholder="hh:mm"
            style="line-height: 3rem"
            type="text"
            @blur="handleBlur"
            @keydown="handleKeydown"
        />
    </form-group>
</template>

<script setup>
import FormGroup from './FormGroup.vue';
import { DateTime } from 'luxon';

const props = defineProps({
    horizontal: Boolean,
    label: String,
    labelClasses: Array,
    required: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    min: DateTime,
    max: DateTime,
    modelValue: DateTime,
    step: {
        type: [Number, String],
        default: 5,
    },
});

const emit = defineEmits(['update:modelValue']);

function normalizeTime(input) {
    const match = input.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) return null;

    let hh = parseInt(match[1]);
    let mm = match[2] !== undefined ? parseInt(match[2]) : 0;

    if (hh > 23 || mm > 59) return null;

    return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
}

function getTimeInMinutes(value) {
    const time = normalizeTime(value);

    if (!time) {
        return 0;
    }

    let [hh, mm] = time.split(':').map(Number);
    return hh * 60 + mm;
}

function setTime(input, timeInMinutes) {
    const hh = Math.floor(timeInMinutes / 60) % 24;
    const mm = timeInMinutes % 60;

    const newDateTime = props.modelValue.set({ hour: hh, minute: mm });
    input.value = newDateTime.toFormat('HH:mm');
    emit('update:modelValue', newDateTime);
}

function increateTime(input) {
    const timeInMinutes = getTimeInMinutes(input.value);
    const step = Number(props.step) || 5;
    setTime(input, timeInMinutes + step);
}

function decreaseTime(input) {
    const timeInMinutes = getTimeInMinutes(input.value);
    const step = Number(props.step) || 5;
    setTime(input, timeInMinutes - step);
}

const handleKeydown = (event) => {
    const key = event.key.toLowerCase();

    if (key.match(/^[0-9:]+$/)) {
        return;
    }

    switch (key) {
        case 'arrowup':
            increateTime(event.target);
            break;
        case 'arrowdown':
            decreaseTime(event.target);
            break;
        case 'arrowleft':
        case 'arrowright':
        case 'backspace':
        case 'delete':
        case 'tab':
            break;
        case 'enter':
        case 'escape':
            event.target.blur();
            break;
        default:
            // Ignore other keys
            event.preventDefault();
            break;
    }
};

const handleBlur = (event) => {
    let value = normalizeTime(event.target.value.trim());

    if (!value) {
        value = props.required ? '00:00' : '';
    }

    const time = DateTime.fromFormat(value, 'HH:mm');
    let newDateTime = props.modelValue.set({ hour: time.hour, minute: time.minute });

    // enforce min/max
    if (props.min && newDateTime < props.min) newDateTime = props.min;
    if (props.max && newDateTime > props.max) newDateTime = props.max;

    event.target.value = newDateTime.toFormat('HH:mm');
    emit('update:modelValue', newDateTime);
};
</script>
